import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Route as RouteIcon, ClipboardList, Send, ImagePlus, X, Languages, Loader2 } from 'lucide-react';
import type { CollaborativeMap, AppUser, CampingSpot, GpxRoute, CollabMessage } from '../../types';
import {
  subscribeToCollabMessages,
  sendCollabMessage,
  markCollabThreadAsRead,
} from '../../services/collaborativeMapsService';
import { translateTesterMessage } from '../../services/firebaseService';
import {
  parseMentions,
  buildMentionToken,
  activeMentionQuery,
  type MentionKind,
} from '../../utils/chatMentions';

interface MentionItem {
  kind: MentionKind;
  id: string;
  label: string;
}

interface CollabChatPanelProps {
  map: CollaborativeMap;
  currentUser: AppUser;
  spots: CampingSpot[];
  routes: GpxRoute[];
  onOpenSpot?: (spotId: string) => void;
  onOpenRoute?: (routeId: string) => void;
  onOpenPackingList?: (listId: string) => void;
}

const KIND_ICON: Record<MentionKind, React.ReactNode> = {
  spot: <MapPin size={11} className="text-emerald-500 flex-shrink-0" />,
  route: <RouteIcon size={11} className="text-sky-500 flex-shrink-0" />,
  list: <ClipboardList size={11} className="text-amber-500 flex-shrink-0" />,
};

const CollabChatPanel: React.FC<CollabChatPanelProps> = ({
  map,
  currentUser,
  spots,
  routes,
  onOpenSpot,
  onOpenRoute,
  onOpenPackingList,
}) => {
  const { t, i18n } = useTranslation();
  const myLang = (i18n.language || 'no').split('-')[0];

  const [messages, setMessages] = useState<CollabMessage[]>([]);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Oversettelse (kun andres meldinger)
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [translatingIds, setTranslatingIds] = useState<Set<string>>(new Set());
  const [preferOriginal, setPreferOriginal] = useState<Set<string>>(new Set());

  // @-mention autocomplete
  const [mention, setMention] = useState<{ query: string; start: number; caret: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // --- Innhold i mappa som kan lenkes ---
  const mentionItems = useMemo<MentionItem[]>(() => {
    const items: MentionItem[] = [];
    for (const id of map.spotIds || []) {
      const s = spots.find(x => x.id === id);
      if (s) items.push({ kind: 'spot', id, label: s.name });
    }
    for (const id of map.routeIds || []) {
      const r = routes.find(x => x.id === id);
      if (r) items.push({ kind: 'route', id, label: r.name });
    }
    for (const [id, pl] of Object.entries(map.packingLists || {})) {
      items.push({ kind: 'list', id, label: pl.name });
    }
    return items;
  }, [map.spotIds, map.routeIds, map.packingLists, spots, routes]);

  const suggestions = useMemo<MentionItem[]>(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return mentionItems
      .filter(i => !q || i.label.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, mentionItems]);

  // --- Abonnér på meldinger ---
  useEffect(() => {
    const unsub = subscribeToCollabMessages(map.id, setMessages);
    return unsub;
  }, [map.id]);

  // Markér som lest når panelet er åpent og det kommer nye meldinger
  useEffect(() => {
    markCollabThreadAsRead(map.id, currentUser.uid).catch(() => {});
  }, [map.id, currentUser.uid, messages.length]);

  // Auto-scroll til bunn
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  // --- Oversettelse ---
  const handleTranslate = async (m: CollabMessage) => {
    if (!m.text) return;
    const cacheKey = `${m.id}_${myLang}`;
    if (translations[cacheKey]) {
      setPreferOriginal(prev => {
        const next = new Set(prev);
        next.has(m.id) ? next.delete(m.id) : next.add(m.id);
        return next;
      });
      return;
    }
    setTranslatingIds(prev => new Set([...prev, m.id]));
    try {
      const translated = await translateTesterMessage(m.text, myLang);
      setTranslations(prev => ({ ...prev, [cacheKey]: translated }));
      setPreferOriginal(prev => { const n = new Set(prev); n.delete(m.id); return n; });
    } catch {
      /* stille feil — behold original */
    } finally {
      setTranslatingIds(prev => { const n = new Set(prev); n.delete(m.id); return n; });
    }
  };

  const displayTextFor = (m: CollabMessage): { value: string; isTranslated: boolean } => {
    const isOther = m.senderId !== currentUser.uid;
    if (!isOther || preferOriginal.has(m.id)) return { value: m.text, isTranslated: false };
    const manual = translations[`${m.id}_${myLang}`];
    if (manual) return { value: manual, isTranslated: true };
    if (m.translatedText && m.translatedTo === myLang) return { value: m.translatedText, isTranslated: true };
    return { value: m.text, isTranslated: false };
  };

  // --- Input + mention-deteksjon ---
  const onTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setText(value);
    const caret = e.target.selectionStart ?? value.length;
    const active = activeMentionQuery(value, caret);
    setMention(active ? { ...active, caret } : null);
  };

  const insertMention = useCallback((item: MentionItem) => {
    if (!mention) return;
    const token = buildMentionToken(item.kind, item.id, item.label);
    const before = text.slice(0, mention.start);
    const after = text.slice(mention.caret);
    const next = `${before}${token} ${after}`;
    setText(next);
    setMention(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [mention, text]);

  const handleSend = async () => {
    if (isSending || (!text.trim() && files.length === 0)) return;
    setIsSending(true);
    setError(null);
    try {
      await sendCollabMessage(map.id, currentUser, text, files);
      setText('');
      setFiles([]);
      setMention(null);
    } catch {
      setError(t('collab.chat.send_failed', 'Kunne ikke sende meldingen. Prøv igjen.'));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && suggestions.length && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      insertMention(suggestions[0]);
      return;
    }
    if (e.key === 'Escape' && mention) { e.preventDefault(); setMention(null); return; }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSend(); }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imgs = Array.from(e.clipboardData?.items || [])
      .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
      .map(it => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (imgs.length) { e.preventDefault(); setFiles(prev => [...prev, ...imgs]); }
  };

  const filePreviews = useMemo(() => files.map(f => ({ name: f.name, url: URL.createObjectURL(f) })), [files]);
  useEffect(() => () => { filePreviews.forEach(p => URL.revokeObjectURL(p.url)); }, [filePreviews]);

  const openMention = (kind: MentionKind, id: string) => {
    if (kind === 'spot') onOpenSpot?.(id);
    else if (kind === 'route') onOpenRoute?.(id);
    else onOpenPackingList?.(id);
  };

  const renderMessageBody = (value: string) => (
    parseMentions(value).map((seg, i) =>
      seg.type === 'text'
        ? <span key={i}>{seg.value}</span>
        : (
          <button
            key={i}
            onClick={() => openMention(seg.kind, seg.id)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-md bg-[var(--bg-input)] border border-[var(--border-secondary)] text-emerald-400 hover:bg-white/8 transition-colors align-middle"
          >
            {KIND_ICON[seg.kind]}
            <span className="text-2xs font-bold">{seg.label}</span>
          </button>
        )
    )
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Meldingsliste */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2.5 pr-1 overscroll-contain" style={{ minHeight: 0 }}>
        {messages.length === 0 ? (
          <p className="text-2xs text-[var(--text-muted)] text-center pt-8">
            {t('collab.chat.empty', 'Ingen meldinger ennå. Skriv den første — bruk @ for å lenke til en plass, rute eller pakkeliste i mappa.')}
          </p>
        ) : (
          messages.map(m => {
            const own = m.senderId === currentUser.uid;
            const { value, isTranslated } = displayTextFor(m);
            const canTranslate = !own && !!m.text;
            const isTranslating = translatingIds.has(m.id);
            return (
              <div key={m.id} className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[82%] rounded-2xl p-2.5 border ${own ? 'bg-sky-600/20 border-sky-500/30' : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)]'}`}>
                  {!own && <p className="text-3xs uppercase tracking-widest font-black opacity-60 mb-1">{m.senderName}</p>}
                  {m.text && (
                    <p className="text-tiny whitespace-pre-wrap leading-relaxed text-[var(--text-primary)]">
                      {renderMessageBody(value)}
                    </p>
                  )}
                  {canTranslate && (
                    <div className="mt-1">
                      {isTranslating ? (
                        <Loader2 size={9} className="animate-spin text-[var(--text-muted)]" />
                      ) : (
                        <button
                          onClick={() => handleTranslate(m)}
                          className="inline-flex items-center gap-1 text-3xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                        >
                          <Languages size={9} />
                          <span>{isTranslated ? t('feedback.view_original', 'Vis original') : t('feedback.translate_btn', 'Oversett')}</span>
                        </button>
                      )}
                    </div>
                  )}
                  {m.imageUrls?.length > 0 && (
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      {m.imageUrls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden border border-[var(--border-secondary)]">
                          <img src={url} alt="" loading="lazy" decoding="async" className="w-full h-20 object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bildepreview */}
      {filePreviews.length > 0 && (
        <div className="flex gap-1.5 flex-wrap pt-2">
          {filePreviews.map((p, i) => (
            <div key={i} className="relative">
              <img src={p.url} alt="" className="w-12 h-12 object-cover rounded-lg border border-[var(--border-secondary)]" />
              <button
                onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
              >
                <X size={9} />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-2xs text-red-400 pt-1">{error}</p>}

      {/* Input med mention-dropdown */}
      <div className="relative pt-2">
        {mention && suggestions.length > 0 && (
          <div className="absolute bottom-full mb-1 left-0 right-0 bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-xl overflow-hidden shadow-xl z-10">
            {suggestions.map(s => (
              <button
                key={`${s.kind}:${s.id}`}
                onClick={() => insertMention(s)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/8 transition-colors"
              >
                {KIND_ICON[s.kind]}
                <span className="text-tiny text-[var(--text-primary)] truncate">{s.label}</span>
                <span className="text-4xs uppercase tracking-widest text-[var(--text-muted)] ml-auto">
                  {s.kind === 'spot' ? t('collab.chat.kind_spot', 'plass') : s.kind === 'route' ? t('collab.chat.kind_route', 'rute') : t('collab.chat.kind_list', 'liste')}
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <label className="p-2.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer flex-shrink-0">
            <ImagePlus size={15} />
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => { setFiles(prev => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ''; }}
            />
          </label>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={onTextChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={1}
            placeholder={t('collab.chat.placeholder', 'Skriv en melding … @ for å lenke innhold')}
            className="flex-1 resize-none bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-tiny text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-sky-500/50 max-h-24"
          />
          <button
            onClick={handleSend}
            disabled={isSending || (!text.trim() && files.length === 0)}
            className="p-2.5 rounded-xl bg-sky-500/20 border border-sky-500/30 text-sky-400 disabled:opacity-40 hover:bg-sky-500/30 transition-all flex-shrink-0"
          >
            {isSending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CollabChatPanel;
